import {
  Activity,
  LogIn,
  Target,
  CalendarClock,
  Phone,
  Briefcase,
  CalendarRange,
  ClipboardList,
  CheckCircle2,
  Search,
  CalendarDays,
  Clock,
  Coffee,
  MessageSquare,
  FileText,
  Bell,
  UserPlus,
  Share2,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeTone } from '@/shared/components/ui/Badge';

export interface ActionMeta {
  label: string;
  icon: LucideIcon;
  tone: BadgeTone;
}

/**
 * Known activity action_type → display metadata. Unknown/future actions fall
 * back to a prefix-based category so the timeline already supports modules
 * whose backend events are added later.
 */
const ACTION_META: Record<string, ActionMeta> = {
  login: { label: 'Logged in', icon: LogIn, tone: 'neutral' },
  lead_status: { label: 'Lead status changed', icon: Target, tone: 'info' },
  follow_up_set: { label: 'Follow-up set', icon: CalendarClock, tone: 'info' },
  call_logged: { label: 'Call logged', icon: Phone, tone: 'neutral' },
  deal_create: { label: 'Deal created', icon: Briefcase, tone: 'success' },
  deal_stage: { label: 'Deal stage changed', icon: Briefcase, tone: 'info' },
  deal_update: { label: 'Deal updated', icon: Briefcase, tone: 'info' },
  deal_delete: { label: 'Deal deleted', icon: Briefcase, tone: 'danger' },
  leave_submit: { label: 'Leave requested', icon: CalendarRange, tone: 'info' },
  leave_admin_review: { label: 'Leave reviewed (admin)', icon: CalendarRange, tone: 'info' },
  leave_sa_review: { label: 'Leave reviewed', icon: CalendarRange, tone: 'info' },
  task_update: { label: 'Task updated', icon: ClipboardList, tone: 'info' },
  task_done: { label: 'Task completed', icon: CheckCircle2, tone: 'success' },
  scan_start: { label: 'Scan started', icon: Search, tone: 'info' },
  scan_done: { label: 'Scan completed', icon: Search, tone: 'success' },
  meeting_create: { label: 'Meeting created', icon: CalendarDays, tone: 'success' },
  meeting_done: { label: 'Meeting completed', icon: CalendarDays, tone: 'success' },
  checkin: { label: 'Checked in', icon: Clock, tone: 'success' },
  checkout: { label: 'Checked out', icon: Clock, tone: 'neutral' },
  break_start: { label: 'Break started', icon: Coffee, tone: 'warning' },
  break_end: { label: 'Break ended', icon: Coffee, tone: 'neutral' },
  comment: { label: 'Comment', icon: MessageSquare, tone: 'neutral' },
  doc_create: { label: 'Document added', icon: FileText, tone: 'success' },
};

const PREFIX_META: { prefix: string; meta: ActionMeta }[] = [
  { prefix: 'lead', meta: { label: 'Lead activity', icon: Target, tone: 'info' } },
  { prefix: 'deal', meta: { label: 'Deal activity', icon: Briefcase, tone: 'info' } },
  { prefix: 'task', meta: { label: 'Task activity', icon: ClipboardList, tone: 'info' } },
  { prefix: 'meeting', meta: { label: 'Meeting activity', icon: CalendarDays, tone: 'info' } },
  { prefix: 'leave', meta: { label: 'Leave activity', icon: CalendarRange, tone: 'info' } },
  { prefix: 'scan', meta: { label: 'Scan activity', icon: Search, tone: 'info' } },
  { prefix: 'break', meta: { label: 'Break', icon: Coffee, tone: 'neutral' } },
  { prefix: 'doc', meta: { label: 'Document', icon: FileText, tone: 'neutral' } },
  { prefix: 'project', meta: { label: 'Project activity', icon: ClipboardList, tone: 'info' } },
  { prefix: 'contact', meta: { label: 'Contact activity', icon: UserPlus, tone: 'info' } },
];

function prettify(action: string): string {
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Resolve display metadata for any action_type, with graceful fallbacks. */
export function resolveAction(action: string): ActionMeta {
  if (ACTION_META[action]) return ACTION_META[action];
  const pref = PREFIX_META.find((p) => action.startsWith(p.prefix));
  if (pref) return { ...pref.meta, label: prettify(action) };
  return { label: prettify(action), icon: Activity, tone: 'neutral' };
}

/** Known activity actions for the feed filter dropdown. */
export const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'All activity' },
  ...Object.keys(ACTION_META).map((a) => ({ value: a, label: ACTION_META[a].label })),
];

// ─── Notification type metadata ───────────────────────────────────────────────

export interface NotifMeta {
  icon: LucideIcon;
  label: string;
}

const NOTIF_META: Record<string, NotifMeta> = {
  task_assigned: { icon: ClipboardList, label: 'Task assigned' },
  demo_assigned: { icon: UserPlus, label: 'Demo assigned' },
  demo_shared: { icon: Share2, label: 'Demo shared' },
};

export function resolveNotif(type: string): NotifMeta {
  return NOTIF_META[type] ?? { icon: Bell, label: prettify(type || 'Notification') };
}

// ─── Entity badge metadata (real activityLog.entity_type) ─────────────────────

export const ENTITY_LABELS: Record<string, string> = {
  lead: 'Lead',
  deal: 'Deal',
  task: 'Task',
  meeting: 'Meeting',
  leave: 'Leave',
  project: 'Project',
  contact: 'Contact',
  demo: 'Demo',
  doc: 'Document',
  note: 'Note',
  announcement: 'Announcement',
  user: 'User',
};

export function entityLabel(type?: string): string | null {
  if (!type) return null;
  return ENTITY_LABELS[type] ?? prettify(type);
}
